import { apiClient } from './configs/axios-setup'

export type RunMediaSource = 'postproc' | 'gif' | 'geometry' | 'mav' | 'upload' | 'markup'

/** A saved media item on a run. */
export interface RunMediaItem {
  id: string
  blobPath: string
  /** Stable identity of the source media (blob path minus SAS query) for dedup. */
  sourceKey?: string
  url?: string
  caption?: string
  type: 'image' | 'video'
  source: RunMediaSource
  caseKey?: string
  caseName?: string
  timeAdded: number
}

export interface RunMediaRecord {
  raceTeamId: string
  runName: string
  project?: string
  items: RunMediaItem[]
  createByUser: string
  createByUserName: string
  timeCreated: number
  timeUpdated: number
}

/** A media item to add to a run (signed source URL + metadata). */
export interface RunMediaInput {
  url: string
  caption?: string
  type?: 'image' | 'video'
  source?: RunMediaSource
  caseKey?: string
  caseName?: string
}

/** Get a run's saved media collection (null when none). */
export const getRunMedia = (runName: string, project?: string): Promise<RunMediaRecord | null> => {
  const params = new URLSearchParams({ runName })
  if (project) params.set('project', project)
  return apiClient
    .get(`/RunMedia?${params.toString()}`)
    .then(res => (res.data.runMedia as RunMediaRecord) ?? null)
}

/** List runs that have saved media. */
export const listRunMedia = (): Promise<
  { runName: string; project?: string; count: number; timeUpdated: number }[]
> => {
  return apiClient.get('/RunMedia/list').then(res => res.data.runs ?? [])
}

/** Add media items to a run's collection. Returns the updated record. */
export const addRunMedia = (
  runName: string,
  items: RunMediaInput[],
  project?: string
): Promise<RunMediaRecord> => {
  return apiClient
    .post('/RunMedia', { runName, project, items })
    .then(res => res.data.runMedia as RunMediaRecord)
}

/** Remove one media item from a run's collection. */
export const removeRunMediaItem = (
  runName: string,
  itemId: string,
  project?: string
): Promise<RunMediaRecord | null> => {
  const query = project ? `?project=${encodeURIComponent(project)}` : ''
  return apiClient
    .delete(`/RunMedia/${encodeURIComponent(runName)}/items/${encodeURIComponent(itemId)}${query}`)
    .then(res => (res.data.runMedia as RunMediaRecord) ?? null)
}
